import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { useDispatch, useSelector } from "react-redux";
import { fetchQuestions, submitAnswer, updateExamDetails } from "../../actions/answerExamSlice";
import "../../css/exam/ExamQuestionPage.css";
import Header from "../Header";
import Footer from "../Footer";
import config from '../../config';
import Loading from "../Loading"

const ExamQuestionPage = () => {
    const { exam_id } = useParams();  // Read exam_id from the URL
    const auth = useAuth();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const user_id = auth?.user?.profile?.["cognito:username"];
    const token = auth?.user?.id_token;

    const { loading, questions, error, answerStatus } = useSelector((state) => state.answerExam);
    const [selectedOption, setSelectedOption] = useState({});
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [showAnswer, setShowAnswer] = useState(false);

    // Prevent multiple API calls
    const hasFetched = useRef(false);

    useEffect(() => {
        if (user_id && exam_id && token && !hasFetched.current) {
            hasFetched.current = true; // Mark as fetched to prevent duplicate calls
            console.log("Calling fetchQuestions...");
            dispatch(fetchQuestions({ user_id, exam_id, fetch_type: "UNSOLVED_QUESTION", token }));
        }
    }, [user_id, exam_id, token, dispatch]);

    const currentQuestion = questions[currentQuestionIndex];

    const handleOptionChange = (questionId, option) => {
        setSelectedOption({ ...selectedOption, [questionId]: option });
    };

    const handleAnswer = () => {
        setShowAnswer(false);
        if (!selectedOption[currentQuestion.question_id]) {
            alert("Please select an option before answering.");
            return;
        }

        const student_option = selectedOption[currentQuestion.question_id];

        dispatch(
            submitAnswer({
                user_id,
                exam_instance_id: currentQuestion.exam_instance_id,
                question_id: currentQuestion.question_id,
                student_option,
                token,
            })
        ).then((action) => {
            if (action.meta.requestStatus === "fulfilled") {
                // Move to the next question
                const nextQuestionIndex = currentQuestionIndex + 1;

                if (nextQuestionIndex < questions.length) {
                    setCurrentQuestionIndex(nextQuestionIndex);
                    setSelectedOption({});
                } else {
                    alert("You have completed the exam.");

                    dispatch(updateExamDetails({ user_id, exam_id, token }))
                    .then((updateAction) => {
                        if (updateAction.meta.requestStatus === "fulfilled") {
                            console.log("Exam details updated successfully:", updateAction.payload);
                        } else {
                            console.error("Failed to update exam details:", updateAction.payload);
                        }
                    });

                    navigate("/account");
                }
            } else {
                alert("Failed to submit the answer. Please try again.");
            }
        });
    };

    const handleQuit = () => {
        setShowAnswer(false);
        navigate("/account");
    };

    if (loading) return <Loading/>;
    if (error) return <p className="error">{error}</p>;
    if (questions.length === 0) return <p>No questions available.</p>;

    return (
        <div className="exam-page-wrapper">
            <Header /> 
            <div className="exam-container">
                <h2 className="title">Exam Question</h2>
                <div className="question-box">
                    {currentQuestion.image ? (
                        <div className="question-layout">
                            <div className="question-image-container">
                                <img
                                    src={config.imageUri + currentQuestion.exam_type +'/'+ currentQuestion.question_type +'/'+ currentQuestion.image}
                                    alt="Question"
                                    className="question-image"
                                />
                            </div>
                            <div className="options-container">
                                <p className="question-text">{currentQuestion.question}</p>
                                {Object.entries(currentQuestion.options).map(([key, value]) => (
                                    <label key={key} className={`option-label ${ showAnswer && key === currentQuestion.answer ? 'correct-answer' : ''}`}>
                                        <input
                                            type="radio"
                                            name="option"
                                            value={key}
                                            checked={selectedOption[currentQuestion.question_id] === key}
                                            onChange={() => handleOptionChange(currentQuestion.question_id, key)}
                                        />
                                        {value} {showAnswer && key === currentQuestion.answer ? '[Correct Answer]' : ''}
                                    </label>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="options-container-no-image">
                            <p className="question-text">{currentQuestion.question}</p>
                            {Object.entries(currentQuestion.options).map(([key, value]) => (
                                <label key={key} className={`option-label ${ showAnswer && key === currentQuestion.answer ? 'correct-answer' : ''}`}>
                                    <input
                                        type="radio"
                                        name="option"
                                        value={key}
                                        checked={selectedOption[currentQuestion.question_id] === key}
                                        onChange={() => handleOptionChange(currentQuestion.question_id, key)}
                                    />
                                    {value} {showAnswer && key === currentQuestion.answer ? '[Correct Answer]' : ''}
                                </label>
                            ))}
                        </div>
                    )}
                </div>
                <div className="button-group">
                    {answerStatus === 'loading' ? <button className="answer-btn" onClick={handleAnswer} disabled="true">Loading</button>
                    : <button className="answer-btn" onClick={handleAnswer}>Submit Answer</button>}
                    
                    <button className="show-answer-btn" onClick={() => showAnswer ? setShowAnswer(false ) : setShowAnswer(true)}>
                        {showAnswer ? 'Hide Answer' : 'Show Answer'}
                    </button>
                    <button className="quit-btn" onClick={handleQuit}>Quit</button>
                </div>
            </div>
            <Footer />
        </div>
    );
};

export default ExamQuestionPage;
